// Mapeo de códigos de error del backend (POST /v2/company/.../templates y
// POST /v2/broadcast) a mensajes accionables en español. Los códigos internos
// del servidor (catch-all, auth de API, desconocidos) caen al mensaje genérico
// con isExpected: false para que el hook los reporte a Sentry.

import {
  templateProviderDiagnostics,
  TEMPLATE_PROVIDER_FALLBACK_MESSAGE,
  TEMPLATE_PROVIDER_POLICY_MESSAGE,
} from "../../../../../utils/template-provider-diagnostics";

const GENERIC_TEMPLATE_MESSAGE = "Error al crear la plantilla, contacte a soporte";
const GENERIC_BROADCAST_MESSAGE = "Error al crear la campaña, contacte a soporte";

const TEMPLATE_ERROR_MESSAGES = {
  INVALID_CHANNEL:
    "El canal seleccionado no permite crear plantillas de WhatsApp, verifica tu canal e intenta de nuevo",
  ERROR_GUPSHUP_API:
    "WhatsApp no aceptó la plantilla, revisa el nombre y el contenido e intenta de nuevo",
  ERROR_GUPSHUP_MEDIA_API:
    "No pudimos procesar el archivo multimedia de la plantilla, verifica el archivo e intenta de nuevo",
  ERROR_DOWNLOADING_FILE:
    "No pudimos descargar el archivo multimedia de la plantilla, verifica el archivo e intenta de nuevo",
};

const BROADCAST_ERROR_MESSAGES = {
  INVALID_CHANNEL_TOKEN:
    "El token del canal es inválido, reconecta tu canal de WhatsApp e intenta de nuevo",
  TEMPLATE_NOT_FOUND: "La plantilla no existe o aún no está aprobada por WhatsApp",
};

// El interceptor de iventas_api_v2 expone errorCode; el shape legacy dejaba el
// data completo ({ error: CODE }) en `message`, por eso el segundo intento.
const getErrorCode = (error) => {
  if (!error) {
    return null;
  }

  if (error.errorCode) {
    return error.errorCode;
  }

  if (error.message && typeof error.message === "object" && error.message.error) {
    return error.message.error;
  }

  return null;
};

const getNonEmptyString = (value) =>
  typeof value === "string" && value.trim().length > 0 ? value : null;

// WA_API_ERROR trae el motivo real de Meta (ya traducido) en data.message; el
// interceptor lo deja en userMessage o dentro del objeto legacy `message`.
const getWaApiErrorMessage = (error) =>
  getNonEmptyString(error?.userMessage) ||
  (error?.message && typeof error.message === "object"
    ? getNonEmptyString(error.message.message)
    : null);

// Solo el mensaje genérico lleva la referencia de soporte del backend; los
// mensajes accionables no la necesitan.
const buildGenericResult = (error, genericMessage) => {
  const supportRef = getNonEmptyString(error?.supportRef);

  return {
    message: supportRef ? `${genericMessage} (ref: ${supportRef})` : genericMessage,
    isExpected: false,
  };
};

const resolveErrorMessage = (error, messagesByCode, genericMessage) => {
  const code = getErrorCode(error);

  if (code && messagesByCode[code]) {
    return { message: messagesByCode[code], isExpected: true };
  }

  return buildGenericResult(error, genericMessage);
};

// Transport provenance takes precedence over Error identity or message text.
export const isTemplateBackendError = (error) => Boolean(error && (
  error.errorCode !== undefined || error.httpCode !== undefined ||
  error.response !== undefined || error.userMessage !== undefined ||
  error.internalCode !== undefined || error.providerCode !== undefined ||
  error.providerSubcode !== undefined || error.supportRef !== undefined ||
  (error.message && typeof error.message === "object")
));

export const resolveTemplateErrorMessage = (error) => {
  const diagnostics = templateProviderDiagnostics(error);
  const code = getErrorCode(error);
  let result;

  if (code && Object.prototype.hasOwnProperty.call(TEMPLATE_ERROR_MESSAGES, code)) {
    result = { message: TEMPLATE_ERROR_MESSAGES[code], isExpected: true };
  } else if (code === "WA_API_ERROR" && diagnostics.providerCode === 368) {
    result = {
      message: TEMPLATE_PROVIDER_POLICY_MESSAGE,
      isExpected: true,
    };
  } else {
    const waMessage = code === "WA_API_ERROR" ? getWaApiErrorMessage(error) : null;
    const isBackendFallback = waMessage === TEMPLATE_PROVIDER_FALLBACK_MESSAGE;
    // Keep legacy provider guidance useful, but text alone is not an expected error.
    result = waMessage && !isBackendFallback
      ? { message: waMessage, isExpected: false }
      : buildGenericResult(diagnostics, GENERIC_TEMPLATE_MESSAGE);
  }

  return Object.keys(diagnostics).length ? { ...result, diagnostics } : result;
};

export const resolveBroadcastErrorMessage = (error) =>
  resolveErrorMessage(error, BROADCAST_ERROR_MESSAGES, GENERIC_BROADCAST_MESSAGE);
